import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLenis } from "lenis/react";
import { Link } from "react-router-dom";
import { ArrowRight, Mail, Layers, Rocket } from "lucide-react";
import { MaskedLines } from "@/components/site/motion";
import { EcosystemVisual } from "@/components/sections/EcosystemVisual";

export const Hero = () => {
  const lenis = useLenis();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yArt = useTransform(scrollYProgress, [0, 1], [0, -70]);

  const goContact = () => {
    const el = document.getElementById("contact");
    if (el && lenis) lenis.scrollTo(el, { offset: -80 });
  };

  return (
    <section ref={ref} data-testid="hero-section" className="relative overflow-hidden pt-32 pb-16 sm:pt-40 sm:pb-20">
      <div className="absolute inset-0 radial-glow" />
      <div className="grain" />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-[1.02fr_0.98fr]">
          {/* Left copy */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full bg-cobalt/10 px-3.5 py-1.5"
              data-testid="hero-badge"
            >
              <Rocket className="h-3.5 w-3.5 text-cobalt" />
              <span className="text-xs font-semibold uppercase tracking-[0.18em] text-cobalt">Coming Soon</span>
            </motion.div>

            <h1 className="mt-7 font-display font-extrabold tracking-tighter text-ink text-[3.1rem] leading-[0.94] sm:text-6xl lg:text-[4.6rem]">
              <MaskedLines lines={["Opening the Door"]} />
              <span className="block overflow-hidden">
                <motion.span
                  className="block text-cobalt"
                  initial={{ y: "110%" }}
                  animate={{ y: "0%" }}
                  transition={{ duration: 0.9, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
                >
                  to Higher Education.
                </motion.span>
              </span>
            </h1>

            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }}>
              <p className="mt-7 max-w-xl text-lg leading-relaxed text-slatey">
                OpenDegree is building a connected platform that helps learners discover, compare and progress through
                education — and helps education providers simplify and modernise how they attract and enrol them.
              </p>
              <p className="mt-4 max-w-xl text-base leading-relaxed text-slatey/90">
                Admissions and recruitment are where we start. Underneath, automation and intelligent workflows cut
                paperwork and delay — with human support available whenever it's needed.
              </p>

              <div className="mt-9 flex flex-wrap items-center gap-3">
                <Link
                  data-testid="hero-start-journey-btn"
                  to="/journey"
                  className="group inline-flex items-center gap-2 rounded-2xl bg-cobalt px-7 py-4 text-[15px] font-semibold text-white shadow-[0_14px_34px_rgba(23,147,90,0.30)] transition-transform duration-200 hover:scale-[1.03] active:scale-95"
                >
                  Start Your Journey
                  <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
                </Link>
                <button
                  data-testid="hero-contact-btn"
                  onClick={goContact}
                  className="group inline-flex items-center gap-2 rounded-2xl border-2 border-cobalt/25 bg-white px-7 py-4 text-[15px] font-semibold text-cobalt transition-colors duration-200 hover:bg-cobalt/5"
                >
                  Contact Us
                  <Mail className="h-4 w-4" />
                </button>
              </div>

              <div className="mt-7 flex items-center gap-2.5" data-testid="hero-trust">
                <Layers className="h-5 w-5 text-cobalt" />
                <span className="text-sm font-semibold text-slatey">Simplify. Connect. Modernise education.</span>
              </div>
            </motion.div>
          </div>

          {/* Right: bespoke ecosystem visual */}
          <motion.div style={{ y: yArt }}>
            <EcosystemVisual />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
